import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse } from "axios";
import { RootState } from "./store";
import { axiosApi } from "../helpers";
import { ENDPOINTS_MODEL, ENDPOINTS_SERVER, HOST, URL_SERVER } from "../constants/api_endpoints";
import { IGetServersProps, IDataServers, IServerState, IGetClusterServerModelsProps } from "../types/serverTypes";
import { IResponse, RequestStatus, ResponseStatus } from "../types/statusTypes";
import { AllModelResponseType } from "@/types/modelTypes";

const initialState: IServerState = {
	dataServers: null,
	serverStatus: RequestStatus.IDLE,
	clusterServer: {
		smc: {
			modelList: [],
			status: RequestStatus.IDLE,
			error: ResponseStatus.SUCCESS,
			message: "",
		},
		see: {
			modelList: [],
			status: RequestStatus.IDLE,
			error: ResponseStatus.SUCCESS,
			message: "",
		},
		spr: {
			modelList: [],
			status: RequestStatus.IDLE,
			error: ResponseStatus.SUCCESS,
			message: "",
		},
		tts: {
			modelList: [],
			status: RequestStatus.IDLE,
			error: ResponseStatus.SUCCESS,
			message: "",
		},
	},
};

// получение данных по всем серверам сервиса
export const getDataServers = createAsyncThunk(
	"servers/data",
	async ({ serviceType }: IGetServersProps): Promise<IDataServers> => {
		const response: AxiosResponse<IDataServers> = await axiosApi.get(`${URL_SERVER}/${ENDPOINTS_SERVER.ADDRESSES}/${serviceType}`);
		return response.data;
	},
	{
		condition: (_, { getState }) => {
			if ((getState() as RootState).server.serverStatus === RequestStatus.LOADING) return false;
		},
	}
);

// получение списка всех моделей (для совместимости)
export const getClusterServerModels = createAsyncThunk("servers/models", async ({ serviceType }: IGetClusterServerModelsProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<AllModelResponseType | IResponse> = await axiosApi.get(`${HOST}/${serviceType}/${ENDPOINTS_MODEL.LIST}`);
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

const serverSlice = createSlice({
	name: "servers",
	initialState,
	reducers: {
		// очистка данных по серверам
		clearDataServers: (state) => {
			state.dataServers = initialState.dataServers;
			state.serverStatus = initialState.serverStatus;
		},
		// очистка списка моделей серверов обработки
		clearClusterServer: (state) => {
			state.clusterServer = initialState.clusterServer;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getDataServers.pending, (state) => {
				state.serverStatus = RequestStatus.LOADING;
			})
			.addCase(getDataServers.fulfilled, (state, action) => {
				state.serverStatus = RequestStatus.IDLE;
				state.dataServers = action.payload;
			})
			.addCase(getDataServers.rejected, (state) => {
				state.serverStatus = RequestStatus.FAILED;
				state.dataServers = null;
			})
			.addCase(getClusterServerModels.pending, (state, action) => {
				state.clusterServer[action.meta.arg.serviceType].status = RequestStatus.LOADING;
			})
			.addCase(getClusterServerModels.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "object" && "models" in action.payload) {
					state.clusterServer[action.meta.arg.serviceType].status = RequestStatus.IDLE;
					if (action.meta.arg.serviceType === "see" && action.payload.pretrained) {
						state.clusterServer[action.meta.arg.serviceType].modelList = [...action.payload.models, ...action.payload.pretrained].sort();
					} else {
						state.clusterServer[action.meta.arg.serviceType].modelList = action.payload.models.sort();
					}
				} else {
					state.clusterServer[action.meta.arg.serviceType].status = RequestStatus.FAILED;
				}
			})
			.addCase(getClusterServerModels.rejected, (state, action) => {
				state.clusterServer[action.meta.arg.serviceType].status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.clusterServer[action.meta.arg.serviceType].error = action.payload.response?.data.error;
					state.clusterServer[action.meta.arg.serviceType].message = action.payload.response?.data.message;
				}
			});
	},
});

export const { clearDataServers, clearClusterServer } = serverSlice.actions;

export const selectDataServers = (state: RootState) => state.server.dataServers;
export const selectClusterServer = (state: RootState) => state.server.clusterServer;

export default serverSlice.reducer;
